import { compose } from "infrastructure/store";
import type { ID } from "types";
import { compareIds, delay } from "util/support";
import { ECommerceOnboardingApplication, JsonableFields } from "consts/onboarding";
import { DateTimeFormatRepository } from "consts/general";
import { useSimpleRequest } from "hooks";
import { FormAdapter } from "hooks/useForm";
import { putECommerceIdentityVerificationSteps, putECommerceUboIdentities } from "features/onboarding/api";
import type {
    OnboardingIdentityVerification,
    UboIdentity,
    WithFieldsComments,
    WithFieldsStatuses,
    WithRequestedChangeFileds
} from "features/onboarding/types";
import { ECommerce } from "ui/forms/Onboarding";
import {
    useRequestedChanges,
    useFieldsStatuses,
    useFieldsComments,
    useApplicationDetailContext,
    useStepStatus,
    useApplicationStatus
} from "ui/organizms/Onboarding/Applications/common";

export const useUBOIdentityVerification = () => {
    const { getTotalStepFields, getStepData, fetchStep } = useApplicationDetailContext();

    const totalStepFields = getTotalStepFields(ECommerceOnboardingApplication.IdentityVerification);
    const data = getStepData<OnboardingIdentityVerification>(ECommerceOnboardingApplication.IdentityVerification);

    const {
        getStepStatus,
        getNestedStepStatus
    } = useStepStatus();

    const {
        updateApplicationStatus
    } = useApplicationStatus();

    const {
        withRequestedChangeFields,
        serializeRequestedChangeFields
    } = useRequestedChanges();

    const {
        parseFieldsStatuses,
        withFieldsStatuses,
        serializeFieldsStatuses
    } = useFieldsStatuses();

    const {
        withFieldsComments,
        serializeFieldsComments
    } = useFieldsComments();

    const request = useSimpleRequest();

    const stepDataWithRequestedChangeFields = compose<OnboardingIdentityVerification>(
        withRequestedChangeFields,
        withFieldsStatuses,
        withFieldsComments
    )(data);

    const withUBOJsonableField = (jsonableField: JsonableFields) => (pki: ID, {
        [jsonableField]: jsonableValue
    }:
        & WithRequestedChangeFileds
        & WithFieldsComments
        & WithFieldsStatuses
    ) => {
        if (!jsonableValue || typeof jsonableValue !== 'object') {
            return null;
        }

        return Object.entries(jsonableValue).reduce((acc, [key, value]) => {
            const [entityId, entityFieldName] = key.split('.');

            if (compareIds(entityId, pki)) {
                return {
                    ...acc,
                    [entityFieldName]: value
                };
            }

            return acc;
        }, {});
    };

    const formProps: ECommerce.UBOIdentityVerification.OnboardingIdentityVerificationForm = {
        isBootstrapDataLoading: false,
        bootstrapData: {},
        initialValues: ECommerce.UBOIdentityVerification.getInitialValues(stepDataWithRequestedChangeFields),
        onSaveOrCreate: async (values, helpers) => {
            const uboIdentitiesResponses = await Promise.all(
                [values.uboIdentities]
                    .flat()
                    .filter(Boolean)
                    .map(uboIdentity => {
                        const {
                            pki,
                            fullName,
                            residentialAddress,
                            nationality,
                            dateOfBirth,
                            fieldsStatuses: uboFieldsStatuses
                        } = uboIdentity!;

                        const entityId = String(pki)
                        const jsonableFields = {
                            requestedChangeFields: values.requestedChangeFields!,
                            fieldsStatuses: values.fieldsStatuses!,
                            fieldsComments: values.fieldsComments!
                        };

                        let fieldsStatuses = withUBOJsonableField(JsonableFields.FieldsStatuses)(
                            entityId,
                            jsonableFields
                        );

                        fieldsStatuses &&= {
                            ...parseFieldsStatuses({
                                fieldsStatuses: uboFieldsStatuses
                            }),
                            ...fieldsStatuses
                        };

                        return request<UboIdentity>(() => putECommerceUboIdentities({
                            pki: entityId,
                            fullName,
                            residentialAddress,
                            nationality: [nationality]
                                .filter(Boolean)
                                .join(','),
                            dateOfBirth: dateOfBirth?.format(DateTimeFormatRepository.Default),
                            requestedChangeFields: serializeRequestedChangeFields({
                                requestedChangeFields: withUBOJsonableField(JsonableFields.RequestedChangeFields)(
                                    entityId,
                                    jsonableFields
                                )
                            }),
                            fieldsStatuses: serializeFieldsStatuses({
                                fieldsStatuses
                            }),
                            fieldsComments: serializeFieldsComments({
                                fieldsComments: withUBOJsonableField(JsonableFields.FieldsComments)(
                                    entityId,
                                    jsonableFields
                                )
                            }),
                            status: getStepStatus({
                                fieldsStatuses
                            }, totalStepFields)
                        }));
                    })
            );

            const response = await request(() => putECommerceIdentityVerificationSteps({
                userId: data.userId,
                requestedChangeFields: serializeRequestedChangeFields(values),
                fieldsStatuses: serializeFieldsStatuses(values),
                fieldsComments: serializeFieldsComments(values),
                status: getNestedStepStatus(
                    uboIdentitiesResponses.map(({ data }) => data)
                )
            }));

            await delay(1000)
                .then(() => fetchStep(ECommerceOnboardingApplication.IdentityVerification))
                .then(() => delay(1000))
                .then(updateApplicationStatus);

            helpers!.setTouched({});

            return FormAdapter.fromResponse(response);
        }
    };

    return {
        ...formProps,
        ...ECommerce.UBOIdentityVerification.useForm(formProps),
        ...stepDataWithRequestedChangeFields
    };
};

export default useUBOIdentityVerification;
